import type { FieldConstraint } from "@tfg/db"
import { t } from "i18next"
import { z } from "zod"

import { CustomFieldConstraint } from "@tfg/types/endpoints/traceability"

export function hasMoreThanOneValue(
  array: string[]
): array is [string, ...string[]] {
  return array.length > 1
}

export const getCustomFieldParser = (
  cfd: {
    constraint: FieldConstraint
    min: number | null
    max: number | null
  },
  enumValues: string[]
) => {
  if (
    cfd.constraint === CustomFieldConstraint.SELECT ||
    cfd.constraint === CustomFieldConstraint.STANDARD
  ) {
    const values: [string, ...string[]] = hasMoreThanOneValue(enumValues)
      ? enumValues
      : [enumValues[0]]

    const parser = z.union([
      z.literal(""),
      z.enum(values, {
        errorMap: (issue) => {
          switch (issue.code) {
            case "invalid_type":
              return { message: t("traceability:requiredError") }
            case "invalid_enum_value":
              return { message: t("traceability:requiredError") }
            default:
              return { message: t("traceability:requiredError") }
          }
        },
      }),
    ])

    if (cfd.constraint === "STANDARD") {
      return parser.optional()
    } else {
      return parser
    }
  } else if (cfd.constraint === CustomFieldConstraint.MULTI_SELECT) {
    const values: [string, ...string[]] = hasMoreThanOneValue(enumValues)
      ? enumValues
      : [enumValues[0]]

    return z
      .array(z.string())
      .nonempty({
        message: t("traceability:requiredError"),
      })
      .pipe(
        z.array(
          z.enum(values, {
            errorMap: (issue) => {
              switch (issue.code) {
                case "invalid_type":
                  return { message: t("traceability:requiredError") }
                case "invalid_enum_value":
                  return { message: t("traceability:requiredError") }
                default:
                  return { message: t("traceability:requiredError") }
              }
            },
          })
        )
      )
      .transform((value) => JSON.stringify(value))
  } else if (cfd.constraint === CustomFieldConstraint.NUMBER) {
    let parser = z.coerce.number({
      invalid_type_error: t("traceability:numberError"),
    })

    if (cfd.min !== null) {
      parser = parser.min(cfd.min, {
        message: t("traceability:minError", { min: cfd.min }),
      })
    }
    if (cfd.max !== null) {
      parser = parser.max(cfd.max, {
        message: t("traceability:maxError", { max: cfd.max }),
      })
    }
    return z
      .string()
      .min(1, { message: t("traceability:requiredError") })
      .pipe(parser.transform(transformDigits))
  } else {
    return z.string()
  }
}

function transformDigits(value: number) {
  const stringValue = value.toString()
  if (stringValue.length <= 1) return stringValue
  if (stringValue.includes(".")) {
    return value.toFixed(2).toString()
  }
  return stringValue
}
